import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AssessmentTemplateActions from "../../components/admin/AssessmentTemplateActions";
import TableScroll from "../../components/TableScroll";
import { fetchCategoryAssessment } from "../../services/assessmentApi";
import { fetchBrands } from "../../services/brandApi";
import { fetchCategories, searchDevices } from "../../services/deviceApi";
import "../../styles/admin-common.css";

const Catalog = () => {
  const [categories, setCategories] = useState([]);
  const [assessments, setAssessments] = useState({});
  const [brandCount, setBrandCount] = useState(0);
  const [modelCount, setModelCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [templateCategoryId, setTemplateCategoryId] = useState("");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const [cats, brands, models] = await Promise.all([
          fetchCategories(),
          fetchBrands(),
          searchDevices({ query: "", size: 1 }),
        ]);
        setCategories(cats);
        setBrandCount(brands.filter((b) => b.isActive).length);
        setModelCount(models.pagination?.totalElements ?? 0);

        const counts = {};
        await Promise.all(
          cats.map(async (cat) => {
            try {
              const a = await fetchCategoryAssessment(cat.id);
              counts[cat.id] = { questions: a.questions?.length || 0, defects: a.defects?.length || 0 };
            } catch {
              counts[cat.id] = { questions: 0, defects: 0 };
            }
          })
        );
        setAssessments(counts);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <div className="admin-page">
      <h2>Catalog hub</h2>
      <p className="subtitle">
        Central catalog for each product type — questions and defects are defined once under{" "}
        <strong>Type template</strong>, then optionally overridden per model.
      </p>
      {error && <p className="error-msg">{error}</p>}

      <div className="stats-grid">
        <div className="stat-card"><span className="stat-label">Categories</span><span className="stat-value">{categories.length}</span></div>
        <div className="stat-card"><span className="stat-label">Brands</span><span className="stat-value">{brandCount}</span></div>
        <div className="stat-card"><span className="stat-label">Models</span><span className="stat-value">{modelCount}</span></div>
      </div>

      <div className="admin-card">
        <h3>Workflow</h3>
        <ol className="workflow-steps">
          <li><strong>Categories</strong> — <Link to="/devices">Manage</Link></li>
          <li><strong>Brands</strong> — <Link to="/brands">Manage</Link></li>
          <li><strong>Series</strong> — <Link to="/series">Group models</Link> (optional)</li>
          <li><strong>Models</strong> — <Link to="/models">Manage</Link></li>
          <li><strong>Product types</strong> — <Link to="/questions">Questions</Link> · <Link to="/defects">Defects</Link></li>
        </ol>
      </div>

      {loading ? <div className="admin-loading">Loading</div> : (
        <div className="admin-card">
          <h3>Central catalog by product type</h3>
          <TableScroll>
          <table className="admin-table">
            <thead><tr><th>Category</th><th>Questions (central)</th><th>Defects (central)</th><th>Actions</th></tr></thead>
            <tbody>
              {categories.map((cat) => {
                const a = assessments[cat.id] || {};
                return (
                  <tr key={cat.id}>
                    <td><strong>{cat.name}</strong></td>
                    <td>{a.questions ?? "—"}</td>
                    <td>{a.defects ?? "—"}</td>
                    <td className="actions-cell">
                      <Link className="admin-btn secondary" to={`/questions?categoryId=${cat.id}`}>Questions</Link>
                      <Link className="admin-btn secondary" to={`/defects?categoryId=${cat.id}`}>Defects</Link>
                      <button type="button" className="admin-btn secondary" onClick={() => setTemplateCategoryId(String(cat.id))}>Template</button>
                      <Link className="admin-btn" to={`/models?categoryId=${cat.id}`}>Models</Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </TableScroll>
        </div>
      )}

      {templateCategoryId && (
        <AssessmentTemplateActions
          categories={categories}
          categoryId={templateCategoryId}
          onDone={async () => {
            try {
              const a = await fetchCategoryAssessment(templateCategoryId);
              setAssessments((prev) => ({ ...prev, [templateCategoryId]: { questions: a.questions?.length || 0, defects: a.defects?.length || 0 } }));
            } catch { /* ignore */ }
          }}
        />
      )}
    </div>
  );
};

export default Catalog;
